import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { useAuth } from '../../hooks/useAuth';
import PasswordInput from '../../components/PasswordInput';

const LoginSchema = Yup.object().shape({
  email: Yup.string().email('Invalid email').required('Email is required'),
  password: Yup.string().required('Password is required'),
});

// Login has just sent a code by the time this screen mounts, so the server's
// resend cooldown is already running. Start the countdown in step with it
// rather than offering a button that would be rejected on the first press.
const RESEND_COOLDOWN_SECONDS = 60;

const TwoFactorForm: React.FC = () => {
  const { verify2FA, resend2FA } = useAuth();
  const [otp, setOtp] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(RESEND_COOLDOWN_SECONDS);

  useEffect(() => {
    if (secondsLeft <= 0) return;
    const timer = setTimeout(() => setSecondsLeft(secondsLeft - 1), 1000);
    return () => clearTimeout(timer);
  }, [secondsLeft]);

  const handleVerify = useCallback(async () => {
    if (otp.length !== 6 || isVerifying) return;
    setIsVerifying(true);
    try {
      await verify2FA(otp);
    } catch {
      // Toast is raised in the auth context; clear the field so the next
      // attempt starts from empty instead of the rejected code.
      setOtp('');
    } finally {
      setIsVerifying(false);
    }
  }, [otp, isVerifying, verify2FA]);

  const handleResend = useCallback(async () => {
    if (secondsLeft > 0 || isResending) return;
    setIsResending(true);
    try {
      const retryAfter = await resend2FA();
      setSecondsLeft(retryAfter);
      setOtp('');
    } catch {
      // Context has already reported it; leave the button available to retry.
    } finally {
      setIsResending(false);
    }
  }, [secondsLeft, isResending, resend2FA]);

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-content">Two-Factor Authentication</h2>
        <p className="mt-2 text-sm text-content-muted">
          Enter the verification code sent to your email
        </p>
      </div>

      <div>
        <label htmlFor="otp" className="label">Verification Code</label>
        <input
          id="otp"
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          autoComplete="one-time-code"
          value={otp}
          onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleVerify();
          }}
          className="input text-center text-2xl tracking-widest"
          placeholder="000000"
          maxLength={6}
          autoFocus
        />
      </div>

      <button
        onClick={handleVerify}
        disabled={otp.length !== 6 || isVerifying}
        className="w-full btn-primary disabled:opacity-50"
      >
        {isVerifying ? 'Verifying...' : 'Verify'}
      </button>

      <div className="text-center text-sm text-content-muted">
        Didn't get a code?{' '}
        {secondsLeft > 0 ? (
          <span aria-live="polite">
            Resend in <span className="tabular-nums font-medium text-content">{secondsLeft}s</span>
          </span>
        ) : (
          <button
            type="button"
            onClick={handleResend}
            disabled={isResending}
            className="font-medium text-brand-600 hover:text-brand-500 disabled:opacity-50"
          >
            {isResending ? 'Sending...' : 'Resend code'}
          </button>
        )}
      </div>
    </div>
  );
};

const Login: React.FC = () => {
  const { login, requires2FA, tempToken } = useAuth();

  if (requires2FA && tempToken) {
    return <TwoFactorForm />;
  }

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-content">Sign in to your account</h2>
        <p className="mt-2 text-sm text-content-muted">
          Or{' '}
          <Link to="/signup" className="font-medium text-brand-600 hover:text-brand-500">
            create a new account
          </Link>
        </p>
      </div>

      <Formik
        initialValues={{ email: '', password: '' }}
        validationSchema={LoginSchema}
        onSubmit={async (values, { setSubmitting }) => {
          try {
            await login(values.email, values.password);
          } catch (error) {
            // Error handled in auth context
          } finally {
            setSubmitting(false);
          }
        }}
      >
        {({ errors, touched, isSubmitting }) => (
          <Form className="space-y-6">
            <div>
              <label htmlFor="email" className="label">
                Email address
              </label>
              <Field
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                className="input"
                placeholder="you@company.com"
              />
              {errors.email && touched.email && (
                <p className="mt-1 text-sm text-red-600">{errors.email}</p>
              )}
            </div>

            <div>
              <label htmlFor="password" className="label">
                Password
              </label>
              <Field
                id="password"
                name="password"
                as={PasswordInput}
                autoComplete="current-password"
                className="input"
                placeholder="••••••••"
              />
              {errors.password && touched.password && (
                <p className="mt-1 text-sm text-red-600">{errors.password}</p>
              )}
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <input
                  id="remember-me"
                  name="remember-me"
                  type="checkbox"
                  className="h-4 w-4 rounded border-border-strong text-brand-600 focus:ring-brand-500"
                />
                <label htmlFor="remember-me" className="ml-2 block text-sm text-content">
                  Remember me
                </label>
              </div>

              <div className="text-sm">
                <Link to="/forgot-password" className="font-medium text-brand-600 hover:text-brand-500">
                  Forgot password?
                </Link>
              </div>
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full btn-primary"
            >
              {isSubmitting ? 'Signing in...' : 'Sign in'}
            </button>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default Login;
